import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';

import { cn } from '../../lib/cn';

const components: Components = {
  h1: ({ children }) => <h1 className="text-3xl font-bold text-text-primary mb-6">{children}</h1>,
  h2: ({ children }) => <h2 className="text-xl font-semibold text-text-primary mt-10 mb-3">{children}</h2>,
  h3: ({ children }) => <h3 className="text-lg font-semibold text-text-primary mt-6 mb-2">{children}</h3>,
  p: ({ children }) => <p className="text-text-secondary leading-7 my-3">{children}</p>,
  ul: ({ children }) => <ul className="list-disc ps-6 my-3 space-y-2 text-text-secondary leading-7">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal ps-6 my-3 space-y-2 text-text-secondary leading-7">{children}</ol>,
  strong: ({ children }) => <strong className="font-semibold text-text-primary">{children}</strong>,
  blockquote: ({ children }) => (
    <blockquote className="border-s-4 border-brand-primary/40 ps-4 my-4 text-text-muted">{children}</blockquote>
  ),
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full text-sm border border-border">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border border-border bg-elevated px-3 py-2 text-start">{children}</th>,
  td: ({ children }) => <td className="border border-border px-3 py-2 text-text-secondary">{children}</td>,
  a: ({ href, children }) =>
    href?.startsWith('/') ? (
      <Link to={href} className="text-brand-primary underline underline-offset-2">{children}</Link>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-brand-primary underline underline-offset-2">
        {children}
      </a>
    ),
};

/** Renders trusted, repo-authored Markdown. Raw HTML is not rendered. */
export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cn('max-w-none', className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
